import { useLingui } from "@lingui/react";
import { useParams } from "react-router-dom";

/**
 * Hook for locale-aware navigation
 * Automatically prefixes paths with current locale if on a locale-prefixed route
 */
export const useLocaleNavigation = () => {
  const { locale } = useParams<{ locale?: string }>();
  const { i18n } = useLingui();

  /**
   * Get a locale-aware path
   * @param path - The path to navigate to (e.g., "/auth/login")
   * @returns Locale-prefixed path if on a locale route, otherwise original path
   */
  const getLocalePath = (path: string): string => {
    // If we're currently on a locale-prefixed route and it's not the default locale
    if (locale && locale !== "en-US") {
      // Ensure path starts with /
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return `/${locale}${normalizedPath}`;
    }

    // For default locale or non-locale routes, return original path
    return path;
  };

  /**
   * Get current locale from URL or i18n
   */
  const currentLocale = locale || i18n.locale || "en-US";

  return {
    getLocalePath,
    currentLocale,
    isLocaleRoute: !!locale,
  };
};
